import axios from 'axios';

// Définir l'URL de base de l'API à partir des variables d'environnement
const API_BASE_URL = import.meta.env.VITE_API;

// Interface pour la structure d'un administrateur
export interface Admin {
  id: string;
  email: string;
  fullName: string;
  default?: boolean; // Facultatif
}

// Interface pour la permission
export interface Permission {
  id: string;
  name: string;
  description: string;
}

// Interface pour une application
export interface Application {
  id: string;
  name: string;
  description: string;
  displayName?: string;
  redirectUris?: string;
  isSyncronized?: boolean;
}

// Interface pour l'association admin-permission
export interface AdminPermission {
  adminId: string;
  permissionId: string;
  applicationId?: string;
}

// Interface pour la réponse de l'API
interface ApiResponse<T> {
  isSuccess: boolean;
  data: T;
  errorMessage?: string;
}

// Création de l'instance apiClient avec intercepteur (adapté d'apiEntreprise)
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Intercepteur de réponse pour gérer la réauthentification automatique
apiClient.interceptors.response.use(
  response => {
    console.log('headerrr new', response?.headers);
    return response;
  },
  async error => {
    console.log("interceptors call here");

    const originalRequest = error.config;
    const is401 = error.response?.status === 401;
    const reauth = error.response?.headers['x-reauth'] === 'true';
    
    console.log("reauth value", reauth);
    console.log("already retrying", !originalRequest._retry);
    console.log("Is401 response value", is401);

    if (is401 && reauth && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log("retry de la requete in process");
      
      try {
        return await apiClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }
    
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour gérer les erreurs
const handleError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    console.error('Erreur d\'API:', error.response?.data || error.message);
  } else {
    console.error('Erreur inattendue:', error);
  }
};

export const createAdmin = async (adminData: {
  fullName: string;
  email: string;
}): Promise<Admin> => {
  try {
    const companyId = localStorage.getItem('company');

    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const url = `/Company/${companyId}/admin`;

    const response = await apiClient.post<ApiResponse<Admin>>(
      url,
      {
        fullName: adminData.fullName,
        email: adminData.email,
        companyId,
      }
    );

    if (response.data.isSuccess && response.data.data) {
      if (import.meta.env.DEV) {
        console.log('Administrateur créé:', response.data.data);
      }
      return response.data.data;
    }

    throw new Error(response.data.errorMessage || 'Failed to create admin');
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const fetchAdmins = async (companyId: string): Promise<Admin[]> => {
  try {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const url = `/Company/${companyId}/admin`;

    const response = await apiClient.get<ApiResponse<Admin | Admin[]>>(url);

    if (response.data.isSuccess && response.data.data) {
      const admins = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      if (import.meta.env.DEV) {
        console.log('Administrateurs récupérés:', admins);
      }
      return admins;
    }

    console.error('Erreur dans la réponse API:', response.data.errorMessage);
    return [];
  } catch (error) {
    handleError(error);
    return [];
  }
};

export const deleteAdmin = async (adminId: string, companyId: string): Promise<boolean> => {
  try {
    if (!adminId || !companyId) {
      throw new Error('Admin ID and Company ID are required');
    }

    const url = `/Company/${companyId}/admin/${adminId}`;

    const response = await apiClient.delete<ApiResponse<boolean>>(url);

    if (response.data.isSuccess) {
      if (import.meta.env.DEV) {
        console.log('Administrateur supprimé avec succès');
      }
      return true;
    }

    throw new Error(response.data.errorMessage || 'Failed to delete admin');
  } catch (error) {
    handleError(error);
    return false;
  }
};

export const fetchCompanyPermissions = async (companyId: string): Promise<Permission[]> => {
  try {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const url = `/Company/${companyId}/permissions`;

    if (import.meta.env.DEV) {
      console.log('URL de récupération des permissions:', `${API_BASE_URL}${url}`);
    }

    const response = await apiClient.get<ApiResponse<Permission[]>>(url);

    if (response.data.isSuccess && response.data.data) {
      return response.data.data;
    }

    console.error('Erreur dans la réponse API:', response.data.errorMessage);
    return [];
  } catch (error) {
    handleError(error);
    console.error('URL:', `${API_BASE_URL}/Company/${companyId}/permissions`);
    return [];
  }
};

export const getAdminPermissions = async (companyId: string, adminId: string): Promise<Permission[]> => {
  try {
    if (!companyId || !adminId) {
      throw new Error('Company ID and Admin ID are required');
    }

    const url = `/Company/${companyId}/admin/${adminId}/permissions`;

    const response = await apiClient.get<ApiResponse<Permission[]>>(url);

    if (response.data.isSuccess && response.data.data) {
      return response.data.data;
    }

    console.error('Erreur dans la réponse API:', response.data.errorMessage);
    return [];
  } catch (error) {
    handleError(error);
    return [];
  }
};

export const fetchApplications = async (companyId?: string): Promise<Application[]> => {
  try {
    const url = companyId ? `/Company/${companyId}/applications` : `/Application`;

    const response = await apiClient.get<ApiResponse<Application[]>>(url);

    if (response.data.isSuccess && response.data.data) {
      return response.data.data;
    }

    console.error('Erreur dans la réponse API:', response.data.errorMessage);
    return [];
  } catch (error) {
    handleError(error);
    return [];
  }
};

export const assignPermission = async (companyId: string, adminId: string, permissionId: string): Promise<boolean> => {
  try {
    if (!companyId || !adminId || !permissionId) {
      throw new Error('Company ID, Admin ID, and Permission ID are required');
    }

    const url = `/Company/${companyId}/admin/${adminId}/permissions`;

    const response = await apiClient.post<ApiResponse<boolean>>(
      url,
      {
        permissionId,
      }
    );

    if (response.data.isSuccess) {
      if (import.meta.env.DEV) {
        console.log('Permission attribuée avec succès');
      }
      return true;
    }

    throw new Error(response.data.errorMessage || 'Failed to assign permission');
  } catch (error) {
    handleError(error);
    return false;
  }
};

export const revokePermission = async (companyId: string, adminId: string, permissionId: string): Promise<boolean> => {
  try {
    if (!companyId || !adminId || !permissionId) {
      throw new Error('Company ID, Admin ID, and Permission ID are required');
    }

    const url = `/Company/${companyId}/admin/${adminId}/permissions`;

    const response = await apiClient.delete<ApiResponse<boolean>>(url, {
      data: {
        permissionId,
      },
    });

    if (response.data.isSuccess) {
      if (import.meta.env.DEV) {
        console.log('Permission révoquée avec succès');
      }
      return true;
    }

    throw new Error(response.data.errorMessage || 'Failed to revoke permission');
  } catch (error) {
    handleError(error);
    return false;
  }
};

// import axios from 'axios';

// // Définir l'URL de base de l'API à partir des variables d'environnement
// const API_BASE_URL = import.meta.env.VITE_API;

// // Interface pour la structure d'un administrateur
// export interface Admin {
//   id: string;
//   email: string;
//   fullName: string;
//   default?: boolean; // Facultatif
// }

// // Interface pour la permission
// export interface Permission {
//   id: string;
//   name: string;
//   description: string;
// }

// // Interface pour une application
// export interface Application {
//   id: string;
//   name: string;
//   description: string;
//   displayName?: string;
//   redirectUris?: string;
//   isSyncronized?: boolean;
// }

// // Interface pour l'association admin-permission
// export interface AdminPermission {
//   adminId: string;
//   permissionId: string;
//   applicationId?: string;
// }

// // Interface pour la réponse de l'API
// interface ApiResponse<T> {
//   isSuccess: boolean;
//   data: T;
//   errorMessage?: string;
// }

// export const createAdmin = async (adminData: {
//   fullName: string;
//   email: string;
// }): Promise<Admin> => {
//   try {
//     const companyId = localStorage.getItem('company');
//     const token = localStorage.getItem('access_token');

//     if (!companyId) {
//       throw new Error('Company ID is required');
//     }
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin`;

//     const response = await axios.post<ApiResponse<Admin>>(
//       url,
//       {
//         fullName: adminData.fullName,
//         email: adminData.email,
//         companyId,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     if (response.data.isSuccess && response.data.data) {
//       if (import.meta.env.DEV) {
//         console.log('Administrateur créé:', response.data.data);
//       }
//       return response.data.data;
//     }

//     throw new Error(response.data.errorMessage || 'Failed to create admin');
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la création de l\'administrateur:', error.response?.data || error.message);
//       throw new Error(error.response?.data?.errorMessage || 'Failed to create admin');
//     }
//     console.error('Erreur inattendue:', error);
//     throw error;
//   }
// };

// export const fetchAdmins = async (companyId: string): Promise<Admin[]> => {
//   try {
//     if (!companyId) {
//       throw new Error('Company ID is required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin`;

//     const response = await axios.get<ApiResponse<Admin | Admin[]>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (response.data.isSuccess && response.data.data) {
//       const admins = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
//       if (import.meta.env.DEV) {
//         console.log('Administrateurs récupérés:', admins);
//       }
//       return admins;
//     }

//     console.error('Erreur dans la réponse API:', response.data.errorMessage);
//     return [];
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la récupération des administrateurs:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return [];
//   }
// };

// export const deleteAdmin = async (adminId: string, companyId: string): Promise<boolean> => {
//   try {
//     if (!adminId || !companyId) {
//       throw new Error('Admin ID and Company ID are required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin/${adminId}`;

//     const response = await axios.delete<ApiResponse<boolean>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (response.data.isSuccess) {
//       if (import.meta.env.DEV) {
//         console.log('Administrateur supprimé avec succès');
//       }
//       return true;
//     }

//     throw new Error(response.data.errorMessage || 'Failed to delete admin');
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la suppression de l\'administrateur:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return false;
//   }
// };

// export const fetchCompanyPermissions = async (companyId: string): Promise<Permission[]> => {
//   try {
//     if (!companyId) {
//       throw new Error('Company ID is required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/permissions`;

//     if (import.meta.env.DEV) {
//       console.log('URL de récupération des permissions:', url);
//     }

//     const response = await axios.get<ApiResponse<Permission[]>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (response.data.isSuccess && response.data.data) {
//       return response.data.data;
//     }

//     console.error('Erreur dans la réponse API:', response.data.errorMessage);
//     return [];
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error(
//         'Erreur lors de la récupération des permissions:',
//         error.response?.data || error.message,
//         'URL:',
//         `${API_BASE_URL}/Company/${companyId}/permissions`
//       );
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return [];
//   }
// };

// export const getAdminPermissions = async (companyId: string, adminId: string): Promise<Permission[]> => {
//   try {
//     if (!companyId || !adminId) {
//       throw new Error('Company ID and Admin ID are required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin/${adminId}/permissions`;

//     const response = await axios.get<ApiResponse<Permission[]>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (response.data.isSuccess && response.data.data) {
//       return response.data.data;
//     }

//     console.error('Erreur dans la réponse API:', response.data.errorMessage);
//     return [];
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la récupération des permissions de l\'administrateur:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return [];
//   }
// };

// export const fetchApplications = async (companyId?: string): Promise<Application[]> => {
//   try {
//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = companyId ? `${API_BASE_URL}/Company/${companyId}/applications` : `${API_BASE_URL}/Application`;

//     const response = await axios.get<ApiResponse<Application[]>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (response.data.isSuccess && response.data.data) {
//       return response.data.data;
//     }

//     console.error('Erreur dans la réponse API:', response.data.errorMessage);
//     return [];
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la récupération des applications:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return [];
//   }
// };

// export const assignPermission = async (companyId: string, adminId: string, permissionId: string): Promise<boolean> => {
//   try {
//     if (!companyId || !adminId || !permissionId) {
//       throw new Error('Company ID, Admin ID, and Permission ID are required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin/${adminId}/permissions`;

//     const response = await axios.post<ApiResponse<boolean>>(
//       url,
//       {
//         permissionId,
//       },
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     if (response.data.isSuccess) {
//       if (import.meta.env.DEV) {
//         console.log('Permission attribuée avec succès');
//       }
//       return true;
//     }

//     throw new Error(response.data.errorMessage || 'Failed to assign permission');
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de l\'attribution de la permission:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return false;
//   }
// };

// export const revokePermission = async (companyId: string, adminId: string, permissionId: string): Promise<boolean> => {
//   try {
//     if (!companyId || !adminId || !permissionId) {
//       throw new Error('Company ID, Admin ID, and Permission ID are required');
//     }

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       throw new Error('No authentication token found');
//     }

//     const url = `${API_BASE_URL}/Company/${companyId}/admin/${adminId}/permissions`;

//     const response = await axios.delete<ApiResponse<boolean>>(url, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//       data: {
//         permissionId,
//       },
//     });

//     if (response.data.isSuccess) {
//       if (import.meta.env.DEV) {
//         console.log('Permission révoquée avec succès');
//       }
//       return true;
//     }

//     throw new Error(response.data.errorMessage || 'Failed to revoke permission');
//   } catch (error) {
//     if (axios.isAxiosError(error)) {
//       console.error('Erreur lors de la révocation de la permission:', error.response?.data || error.message);
//     } else {
//       console.error('Erreur inattendue:', error);
//     }
//     return false;
//   }
// };

